import { useState, useEffect } from "react"
import { clanAPI, pointsAPI } from "@/lib/api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table"

/* ── Punten-activiteit (alle spelers, nieuwste eerst) ───────────
   Toont elke +/- op elke user: kassa-refunds, prijzenkraam,
   toernooi-awards, manuele adjustments. Zelfde bron als per-user
   historiek (points_transactions), maar globaal. */
function PointsActivity() {
  const [rows, setRows] = useState([])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const load = async () => {
    setLoading(true)
    try {
      const r = await pointsAPI.getAllTransactions(500)
      setRows(r.data.transactions || [])
    } catch { setRows([]) }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const filtered = rows.filter(t =>
    !q ||
    (t.username || '').toLowerCase().includes(q.toLowerCase()) ||
    (t.reason || '').toLowerCase().includes(q.toLowerCase()))

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="text-sm">Punten-activiteit ({rows.length})</CardTitle>
        <div className="flex gap-2 items-center">
          <Input value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Zoek user of reden…"
                 className="h-8 text-xs w-56" />
          <Button size="sm" variant="outline" onClick={load}>
            {loading ? '…' : '↻'} Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-96 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Wanneer</TableHead>
                <TableHead className="w-40">Speler</TableHead>
                <TableHead className="text-right w-20">Δ</TableHead>
                <TableHead>Reden</TableHead>
                <TableHead className="w-24">Type</TableHead>
                <TableHead className="w-32">Door</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-4">
                  Geen transacties.
                </TableCell></TableRow>
              )}
              {filtered.map(t => (
                <TableRow key={t.id}>
                  <TableCell className="text-[10px] font-mono text-muted-foreground">
                    {new Date(t.created_at).toLocaleString('nl-BE')}
                  </TableCell>
                  <TableCell className="text-xs font-semibold truncate">{t.display_name || t.username}</TableCell>
                  <TableCell className={`text-right font-mono text-sm ${t.amount >= 0 ? 'text-emerald-400' : 'text-signal'}`}>
                    {t.amount >= 0 ? '+' : ''}{t.amount}
                  </TableCell>
                  <TableCell className="text-xs truncate">{t.reason || '—'}</TableCell>
                  <TableCell className="text-[10px] pf-mono uppercase text-muted-foreground">{t.transaction_type}</TableCell>
                  <TableCell className="text-xs text-muted-foreground truncate">{t.created_by_username || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

export default function ClanTab({ isSuperAdmin = false }) {
  const [status, setStatus]     = useState(null)
  const [users, setUsers]       = useState([])
  const [members, setMembers]   = useState([])
  const [kassaCards, setKassaCards] = useState([])
  const [balances, setBalances] = useState({})   // userId → balance
  const [syncing, setSyncing]   = useState(false)
  const [autoing, setAutoing]   = useState(false)
  const [msg, setMsg]           = useState(null)
  const [search, setSearch]     = useState('')

  // Points modal state
  const [pointsModal, setPointsModal] = useState(null)  // { user } | null
  const [ptAmount, setPtAmount]       = useState('')
  const [ptReason, setPtReason]       = useState('')
  const [ptSign, setPtSign]           = useState('+')   // '+' | '-'
  const [ptSaving, setPtSaving]       = useState(false)
  const [ptMsg, setPtMsg]             = useState(null)

  // History modal state
  const [histModal, setHistModal]   = useState(null)  // { user, transactions, balance } | null
  const [histLoading, setHistLoading] = useState(false)

  const load = async () => {
    try {
      const [s, u, m, b, k] = await Promise.all([
        clanAPI.getStatus(),
        clanAPI.getUsers(),
        clanAPI.getMembers(),
        pointsAPI.getAllBalances(),
        clanAPI.getKassaCards().catch(() => ({ data: { cards: [] } })),
      ])
      setStatus(s.data)
      setUsers(u.data.users)
      setMembers(m.data.members)
      setKassaCards(k.data?.cards || [])
      const bMap = {}
      for (const row of (b.data.users || [])) bMap[row.id] = row.balance
      setBalances(bMap)
    } catch { setMsg({ type: 'error', text: 'Laden mislukt' }) }
  }

  useEffect(() => { load() }, [])

  const sync = async () => {
    setSyncing(true); setMsg(null)
    try {
      const r = await clanAPI.sync()
      setMsg({ type: 'ok', text: `${r.data.synced} leden gesynchroniseerd van TrueNAS` })
      await load()
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Sync mislukt' })
    } finally { setSyncing(false) }
  }

  const autoLink = async () => {
    setAutoing(true); setMsg(null)
    try {
      const r = await clanAPI.autoLink()
      setMsg({ type: 'ok', text: `${r.data.linked} gebruikers automatisch gekoppeld` })
      await load()
    } catch { setMsg({ type: 'error', text: 'Auto-koppelen mislukt' }) }
    finally { setAutoing(false) }
  }

  const link = async (userId, uid, source) => {
    try {
      await clanAPI.linkUser(userId, uid, source)
      await load()
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Koppelen mislukt' })
    }
  }

  const unlink = async (userId) => {
    try {
      await clanAPI.unlinkUser(userId)
      await load()
    } catch { setMsg({ type: 'error', text: 'Ontkoppelen mislukt' }) }
  }

  const openPoints = (user) => {
    setPointsModal({ user })
    setPtAmount('')
    setPtReason('')
    setPtSign('+')
    setPtMsg(null)
  }

  const submitPoints = async () => {
    const parsed = parseInt(ptAmount)
    if (!ptAmount || isNaN(parsed) || parsed <= 0) {
      setPtMsg({ type: 'error', text: 'Voer een geldig bedrag in (> 0)' }); return
    }
    if (!ptReason.trim()) {
      setPtMsg({ type: 'error', text: 'Reden is verplicht' }); return
    }
    setPtSaving(true); setPtMsg(null)
    try {
      const amount = ptSign === '-' ? -parsed : parsed
      const r = await pointsAPI.addTransaction(pointsModal.user.id, { amount, reason: ptReason.trim() })
      setBalances(prev => ({ ...prev, [pointsModal.user.id]: r.data.balance }))
      setPtMsg({ type: 'ok', text: `Nieuw saldo: ${r.data.balance} punten` })
      setPtAmount(''); setPtReason('')
    } catch (e) {
      setPtMsg({ type: 'error', text: e.response?.data?.error || 'Fout bij opslaan' })
    } finally { setPtSaving(false) }
  }

  const openHistory = async (user) => {
    setHistLoading(true)
    setHistModal({ user, transactions: [], balance: balances[user.id] ?? 0 })
    try {
      const r = await pointsAPI.getUserTransactions(user.id)
      setHistModal({ user, transactions: r.data.transactions, balance: r.data.balance })
    } catch { setHistModal(h => h ? { ...h, error: true } : null) }
    finally { setHistLoading(false) }
  }

  const deleteTx = async (userId, txId) => {
    try {
      const r = await pointsAPI.deleteTransaction(userId, txId)
      setBalances(prev => ({ ...prev, [userId]: r.data.balance }))
      setHistModal(h => h ? { ...h, balance: r.data.balance, transactions: h.transactions.filter(t => t.id !== txId) } : null)
    } catch { setMsg({ type: 'error', text: 'Verwijderen mislukt' }) }
  }

  const clearHistory = async (userId) => {
    if (!confirm('Weet je zeker dat je de volledige transactiegeschiedenis wilt wissen? Het saldo wordt 0.')) return
    try {
      await pointsAPI.clearTransactions(userId)
      setBalances(prev => ({ ...prev, [userId]: 0 }))
      setHistModal(h => h ? { ...h, balance: 0, transactions: [] } : null)
    } catch { setMsg({ type: 'error', text: 'Wissen mislukt' }) }
  }

  const filtered = users.filter(u =>
    !search || u.username.toLowerCase().includes(search.toLowerCase()) ||
    (u.clan_naam || '').toLowerCase().includes(search.toLowerCase())
  )

  const linked   = users.filter(u => u.clan_uid).length
  const unlinked = users.filter(u => !u.clan_uid).length

  return (
    <div className="space-y-6">
      {/* Status cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Clan leden (cache)', value: status?.totalClanMembers ?? '–' },
          { label: 'LAN gebruikers', value: status?.totalLanUsers ?? '–' },
          { label: 'Gekoppeld', value: linked, color: 'text-green-400' },
          { label: 'Niet gekoppeld', value: unlinked, color: 'text-yellow-400' },
        ].map(c => (
          <Card key={c.label}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{c.label}</p>
              <p className={`text-2xl font-bold ${c.color || ''}`}>{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Synchronisatie & Koppeling</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-3 items-center">
          {isSuperAdmin ? (
            <>
              <Button onClick={sync} disabled={syncing} size="sm" variant="outline">
                {syncing ? 'Bezig…' : '↻ Sync van TrueNAS'}
              </Button>
              <Button onClick={autoLink} disabled={autoing || !status?.totalClanMembers} size="sm" variant="outline">
                {autoing ? 'Bezig…' : '⚡ Auto-koppelen op naam'}
              </Button>
            </>
          ) : (
            <span className="text-xs text-muted-foreground italic">Alleen superadmin kan synchroniseren en koppelen.</span>
          )}
          {status?.lastSync && (
            <span className="text-xs text-muted-foreground">
              Laatste sync: {new Date(status.lastSync).toLocaleString('nl-BE')}
            </span>
          )}
          {msg && (
            <span className={`text-xs px-2 py-1 rounded ${msg.type === 'ok' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
              {msg.text}
            </span>
          )}
        </CardContent>
      </Card>

      {/* User table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-sm">Gebruikers & Punten</CardTitle>
            <Input placeholder="Zoek gebruiker of clan naam…" value={search}
              onChange={e => setSearch(e.target.value)} className="max-w-64 h-8 text-xs" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>LAN gebruiker</TableHead>
                <TableHead>Clan naam</TableHead>
                <TableHead>Saldo</TableHead>
                <TableHead>Limiet</TableHead>
                <TableHead>Alcohol</TableHead>
                {isSuperAdmin && <TableHead className="w-48">Koppelen aan</TableHead>}
                {isSuperAdmin && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(u => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{u.display_name || u.username}</p>
                      <p className="text-xs text-muted-foreground">{u.email || u.role}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {u.clan_uid ? (
                      <div>
                        <p className="text-sm font-medium text-green-400">
                          {[u.clan_voornaam, u.clan_naam].filter(Boolean).join(' ') || u.clan_naam}
                        </p>
                        <p className="text-[10px] text-muted-foreground font-mono">{u.clan_uid}</p>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-sm font-semibold ${(balances[u.id] ?? 0) > 0 ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                        {balances[u.id] ?? 0}
                      </span>
                      <span className="text-[10px] text-muted-foreground">pt</span>
                      <div className="flex gap-1 ml-1">
                        <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-yellow-400 hover:text-yellow-300"
                          title="Punten toevoegen/aftrekken" onClick={() => openPoints(u)}>+</Button>
                        <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-xs text-muted-foreground hover:text-foreground"
                          title="Transactiegeschiedenis" onClick={() => openHistory(u)}>≡</Button>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {u.limiet != null ? `€${Number(u.limiet).toFixed(2)}` : '–'}
                  </TableCell>
                  <TableCell>
                    {u.clan_uid
                      ? <span className={`text-xs px-1.5 py-0.5 rounded ${u.alcohol_restrictie ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'}`}>
                          {u.alcohol_restrictie ? 'Beperkt' : 'OK'}
                        </span>
                      : '–'}
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell>
                      <select
                        className="w-full bg-background border border-border rounded px-2 py-1 text-xs text-foreground focus:outline-none focus:border-neon-blue/50"
                        value={u.clan_uid || ''}
                        onChange={e => {
                          const v = e.target.value
                          if (!v) { unlink(u.id); return }
                          // Waarde-format: 'kassa:<uid>' voor kassa-cards, anders TrueNAS-uid.
                          if (v.startsWith('kassa:')) link(u.id, v.slice(6), 'kassa')
                          else                        link(u.id, v)
                        }}
                      >
                        <option value="">— Selecteer kaart —</option>
                        {members.length > 0 && (
                          <optgroup label={`Clan (TrueNAS) · ${members.length}`}>
                            {members.map(m => (
                              <option key={m.uid} value={m.uid}>
                                {m.naam}{m.voornaam ? ` (${m.voornaam})` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {kassaCards.length > 0 && (
                          <optgroup label={`Kassa (nieuwe kaarten) · ${kassaCards.length}`}>
                            {kassaCards.map(c => (
                              <option key={`kassa-${c.uid}`} value={`kassa:${c.uid}`}>
                                {c.naam} · {c.uid}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </TableCell>
                  )}
                  {isSuperAdmin && (
                    <TableCell>
                      {u.clan_uid && (
                        <Button size="sm" variant="ghost" className="text-xs text-red-400 hover:text-red-300 h-7 px-2"
                          onClick={() => unlink(u.id)}>
                          ×
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground text-sm py-8">
                  {status?.totalClanMembers === 0 ? 'Voer eerst een sync uit om clan leden te laden.' : 'Geen resultaten.'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Points modal */}
      {pointsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={e => e.target === e.currentTarget && setPointsModal(null)}>
          <div className="bg-card border border-neon-yellow/30 rounded-xl p-6 w-full max-w-md shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base">Punten beheren</h3>
                <p className="text-xs text-muted-foreground">{pointsModal.user.display_name || pointsModal.user.username}</p>
              </div>
              <span className="text-2xl font-bold text-yellow-400">{balances[pointsModal.user.id] ?? 0} pt</span>
            </div>
            <div className="space-y-3">
              <div className="flex gap-2">
                <button onClick={() => setPtSign('+')}
                  className={`flex-1 py-2 rounded text-sm font-semibold border transition-colors ${ptSign === '+' ? 'bg-green-500/20 border-green-500 text-green-400' : 'border-border text-muted-foreground hover:border-green-500/50'}`}>
                  + Toevoegen
                </button>
                <button onClick={() => setPtSign('-')}
                  className={`flex-1 py-2 rounded text-sm font-semibold border transition-colors ${ptSign === '-' ? 'bg-red-500/20 border-red-500 text-red-400' : 'border-border text-muted-foreground hover:border-red-500/50'}`}>
                  − Aftrekken
                </button>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Aantal punten</label>
                <Input type="number" min="1" placeholder="bijv. 50" value={ptAmount}
                  onChange={e => setPtAmount(e.target.value)}
                  className="bg-background border-neon focus:border-neon-yellow/50" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Reden</label>
                <Input placeholder="bijv. Toernooi gewonnen" value={ptReason}
                  onChange={e => setPtReason(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submitPoints()}
                  className="bg-background border-neon focus:border-neon-yellow/50" />
              </div>
              {ptMsg && (
                <p className={`text-xs px-2 py-1 rounded ${ptMsg.type === 'ok' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                  {ptMsg.text}
                </p>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={submitPoints} disabled={ptSaving} size="sm"
                className="flex-1 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/40">
                {ptSaving ? 'Bezig…' : 'Opslaan'}
              </Button>
              <Button onClick={() => setPointsModal(null)} size="sm" variant="outline" className="flex-1">Sluiten</Button>
            </div>
          </div>
        </div>
      )}

      {/* History modal */}
      <PointsActivity />

      {histModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={e => e.target === e.currentTarget && setHistModal(null)}>
          <div className="bg-card border border-neon/30 rounded-xl p-6 w-full max-w-lg shadow-2xl space-y-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="font-bold text-base">Transactiegeschiedenis</h3>
                <p className="text-xs text-muted-foreground">{histModal.user.display_name || histModal.user.username}</p>
              </div>
              <span className="text-2xl font-bold text-yellow-400">{histModal.balance} pt</span>
            </div>
            <div className="overflow-y-auto flex-1 space-y-2 pr-1">
              {histLoading ? (
                <p className="text-center text-muted-foreground text-sm py-4">Laden…</p>
              ) : histModal.transactions.length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-4">Geen transacties.</p>
              ) : histModal.transactions.map(t => (
                <div key={t.id} className="flex items-start justify-between gap-3 p-2.5 rounded-lg border border-border/50 bg-background/50 group">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{t.reason}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {t.created_by_username ? `Door ${t.created_by_display || t.created_by_username} · ` : ''}
                      {new Date(t.created_at).toLocaleString('nl-BE')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={`text-sm font-bold ${t.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {t.amount > 0 ? '+' : ''}{t.amount}
                    </span>
                    <button
                      onClick={() => deleteTx(histModal.user.id, t.id)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400 transition-opacity text-xs px-1"
                      title="Verwijder deze transactie"
                    >✕</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {histModal.transactions.length > 0 && (
                <Button onClick={() => clearHistory(histModal.user.id)} size="sm" variant="outline"
                  className="text-red-400 border-red-500/30 hover:bg-red-500/10 hover:text-red-300">
                  Wis alles
                </Button>
              )}
              <Button onClick={() => setHistModal(null)} size="sm" variant="outline" className="flex-1">Sluiten</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

